import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TruckIcon as Truck, ArrowRightIcon as ArrowRight, ClockIcon as Clock, CheckCircleIcon as CheckCircle, WarningIcon as Warning } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Dashboard banner that nudges `transporter`-persona users along the
 * Armstrong Transport application funnel.
 *
 * Why this lives separately from the persona greeting:
 *   - Picking the "transporter" persona is a soft signal (just tailors
 *     dashboard copy). Becoming a *verified* transporter who can bid
 *     on real routes requires the full /transport/apply flow (USDA
 *     license + insurance proof + agreement v1.0 click-through +
 *     Stripe Connect onboarding).
 *   - Without this nudge, a user could pick "transporter" and then
 *     wonder why no bid invitations ever appear. This card closes
 *     the loop with one obvious next step.
 *
 * State machine (mirrors /api/me/transporter/application-status):
 *   - state === "none"        → "Finish your application to start bidding"
 *   - state === "pending"     → "Application received · we'll email you"
 *   - state === "needs_info"  → "Action needed on your application"
 *   - state === "approved"    → component hides itself (verified — no nudge)
 *   - state === "rejected"    → polite explanation, no CTA
 *
 * The component is intentionally a no-op for non-transporter personas.
 * It also no-ops on fetch errors so the dashboard never breaks.
 */
export default function TransporterApplicationNudge({ persona }) {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (persona !== "transporter") return;
    // Defensive guard — Sentry caught a "Cannot read properties of
    // undefined (reading 'get')" here when the `api` import resolved
    // to undefined during a hot-reload swap. Belt + suspenders.
    if (!api || typeof api.get !== "function") {
      // Leave a breadcrumb so we still see this in Sentry if it
      // recurs in prod — the guard prevents the crash, but we don't
      // want it to fail silently forever.
      try {
        // eslint-disable-next-line global-require
        import("@sentry/react").then((Sentry) => {
          Sentry.addBreadcrumb({
            category: "guard",
            level: "warning",
            message: "TransporterApplicationNudge: api import undefined — guard fired",
          });
        }).catch(() => { /* sentry not installed — no-op */ });
      } catch (_) { /* no-op */ }
      return;
    }
    let cancelled = false;
    api
      .get("/me/transporter/application-status")
      .then((r) => { if (!cancelled) setStatus(r?.data || null); })
      .catch(() => { /* silent — non-critical signal */ });
    return () => { cancelled = true; };
  }, [persona]);

  if (persona !== "transporter") return null;
  if (!status) return null;
  // Verified transporter — they already see the Transport Hub
  // surfaces; a nudge would only add noise.
  if (status.state === "approved") return null;

  let icon, eyebrow, headline, body, cta;
  if (status.state === "none") {
    icon = Truck;
    eyebrow = "Armstrong Transport";
    headline = "Finish your application to start bidding on routes";
    body = "You're set to see transporter-tailored content. Submit your USDA license, insurance, and signed agreement to receive bid invitations from real buyers.";
    cta = { label: "Start application", to: "/transport/apply" };
  } else if (status.state === "needs_info") {
    icon = Warning;
    eyebrow = "Action needed";
    headline = "We need a quick clarification on your application";
    body = "Our review team flagged something. Open your application to see what's needed — usually a clearer license photo or a fresh certificate of insurance.";
    cta = { label: "Open application", to: "/transport/apply" };
  } else if (status.state === "rejected") {
    icon = Warning;
    eyebrow = "Application status";
    headline = "Your application wasn't approved this round";
    body = "You can reapply once the issue noted in your review email is resolved (typically a license or insurance gap).";
    cta = null;
  } else {
    // pending / unknown but not approved
    icon = Clock;
    eyebrow = "Under review";
    headline = "Your transporter application is in review";
    body = "We aim to review every application within 3 business days. We'll email you the moment a decision is in — no need to refresh.";
    cta = { label: "View application", to: "/transport/apply" };
  }
  const Icon = icon;
  const isApproved = false; // kept for symmetry; approved exits early above

  return (
    <section
      className="mb-8 rounded-2xl border border-[#E8D9A6] bg-cream/60 p-5 sm:p-6"
      data-testid="dashboard-transporter-nudge"
    >
      <div className="flex items-start gap-3">
        <div className={`shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${
          isApproved ? "bg-[#0F8043]/15 text-[#0F8043]" : "bg-gold/15 text-gold-dark"
        }`}>
          <Icon size={20} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-dark mb-1">
            {eyebrow}
          </p>
          <h3 className="font-serif text-lg sm:text-xl text-ink leading-snug">
            {headline}
          </h3>
          <p className="text-[13px] text-[#4A4A4A] mt-1.5 leading-relaxed max-w-2xl">
            {body}
          </p>
          {cta && (
            <Link
              to={cta.to}
              className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-ink hover:bg-crimson text-white text-sm font-semibold px-4 py-2 transition-colors"
              data-testid="dashboard-transporter-nudge-cta"
            >
              <CheckCircle size={14} weight="bold" />
              {cta.label}
              <ArrowRight size={14} weight="bold" />
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
